// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { organizationResponseSchema } from '@/account/api/types';
import { OrganizationAuditCard } from '@/account/components/organization-audit-card';
import { OrganizationBillingDefaultsCard } from '@/account/components/organization-billing-defaults-card';
import { OrganizationDeleteAuditCard } from '@/account/components/organization-delete-audit-card';
import { OrganizationDetailsCard } from '@/account/components/organization-details-card';
import { OrganizationExternalUsersCard } from '@/account/components/organization-external-users-card';
import { OrganizationLedgerAccountsCard } from '@/account/components/organization-ledger-accounts-card';
import { OrganizationLogisticsCard } from '@/account/components/organization-logistics-card';
import { OrganizationLogoCard } from '@/account/components/organization-logo-card';
import { OrganizationUsersCard } from '@/account/components/organization-users-card';
import { OrganizationWorkflowsCard } from '@/account/components/organization-workflows-card';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { User } from '@/authentication/api/types';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { LoadingIndicator } from '@/components/navigation-layout';
import { PageContent } from '@/components/page-content';
import { PageLayout } from '@/components/page-layout';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Stack } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { omit } from 'lodash-es';
import { Suspense, useEffect } from 'react';
import { z } from 'zod';

// Schema for the organization page search parameters
// Accepts ?users, ?users=, ?users=true - all treated as true
const organizationSearchSchema = z.object({
  users: z.preprocess(
    (val) => (val !== undefined ? true : undefined),
    z.boolean().optional()
  ),
});

export const Route = createFileRoute('/_app/organization')({
  component: OrganizationRoute,
  validateSearch: organizationSearchSchema,
});

interface Props {
  user: User;
  isUsersDrawerOpen: boolean;
  onUsersDrawerOpen: () => void;
  onUsersDrawerClose: () => void;
}

function OrgSettings(props: Props) {
  const orgKey = props.user.organization.key;
  const location = useLocation();
  const { data } = useSuspenseQuery({
    queryKey: ['organization', orgKey],
    queryFn: async () => {
      const url = `/v1/organization/${orgKey}`;
      const response = await api.get(url);
      const result = await parseResponse(
        organizationResponseSchema,
        response.data,
        { schemaName: 'Organization', endpoint: url }
      );

      return result.data;
    },
  });

  // Handle hash scrolling
  useEffect(() => {
    const hash = location.hash;
    if (!hash || !data) {
      return;
    }

    // Remove the # symbol if present
    const elementId = hash.replace('#', '');

    // Use double requestAnimationFrame to ensure DOM is fully painted
    requestAnimationFrame(() => {
      const element = document.getElementById(elementId);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    });
  }, [location.hash, data]);

  return (
    <Stack spacing={3}>
      <OrganizationDetailsCard organization={data} />
      <OrganizationLogoCard organization={data} />
      <Suspense fallback={<LoadingIndicator />}>
        <OrganizationLogisticsCard organization={data} />
      </Suspense>
      <OrganizationBillingDefaultsCard organization={data} />
      <Box id="custom-notifications">
        <OrganizationExternalUsersCard organization={data} />
      </Box>
      <Box id="project-workflows">
        <OrganizationWorkflowsCard />
      </Box>
      <Box id="ledger-accounts">
        <OrganizationLedgerAccountsCard orgKey={data.key} />
      </Box>
      <OrganizationUsersCard
        organization={data}
        isOpen={props.isUsersDrawerOpen}
        onOpen={props.onUsersDrawerOpen}
        onClose={props.onUsersDrawerClose}
      />

      <OrganizationAuditCard organizationKey={data.key} />
      <OrganizationDeleteAuditCard organizationKey={data.key} />
    </Stack>
  );
}

function OrganizationRoute() {
  const { user } = useCurrentUser();
  const navigate = Route.useNavigate();
  const searchParameters = Route.useSearch();

  // Manage drawer state locally, initialized from URL on mount
  const usersDrawer = useDisclosure(Boolean(searchParameters.users));

  const handleUsersDrawerOpen = () => {
    usersDrawer.open();
    navigate({ search: { ...searchParameters, users: true } });
  };

  const handleUsersDrawerClose = () => {
    usersDrawer.close();
    navigate({ search: omit(searchParameters, 'users') });
  };

  return (
    <PageLayout title="Organization settings">
      <PageContent>
        <Suspense fallback={<LoadingIndicator />}>
          <OrgSettings
            user={user}
            isUsersDrawerOpen={usersDrawer.isOpen}
            onUsersDrawerOpen={handleUsersDrawerOpen}
            onUsersDrawerClose={handleUsersDrawerClose}
          />
        </Suspense>
      </PageContent>
    </PageLayout>
  );
}
